import { RefObject, useContext, useEffect, useMemo, useRef } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { TableWindowWrapperContext, useLayoutMainContext } from "@ryde/redux";
import {
  buildColumnVisibilityLayoutSignature,
  groupWithFilteredRowsSF,
  pageEntityAtom,
  tableColumnsVisibilityAF,
  tableElementRefAtom,
} from "@ryde/shared";

interface IUsePagingStickyEmulationArguments {
  pagingRef: RefObject<HTMLDivElement>;
}

export const usePagingStickyEmulation = ({
  pagingRef,
}: IUsePagingStickyEmulationArguments): null => {
  const entity = useRecoilValue(pageEntityAtom);
  const groupWithFilteredRows = useRecoilValue(groupWithFilteredRowsSF(entity));
  const columnVisibility = useRecoilValue(tableColumnsVisibilityAF(entity));
  const { mainElement } = useLayoutMainContext();
  const { outerElement } = useContext(TableWindowWrapperContext);
  const columnVisibilitySignature =
    buildColumnVisibilityLayoutSignature(columnVisibility);
  const visibleColumnNumber = useMemo(
    () => Object.values(columnVisibility).filter(Boolean).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnVisibilitySignature is a stable proxy: recompute only when visible column count actually changes, not on every new visibility object reference
    [columnVisibilitySignature],
  );
  const relativeElement =
    groupWithFilteredRows.length > 1 ? outerElement : mainElement;
  const prevVisibleColumnNumber = useRef<number>(visibleColumnNumber);
  const table = useRecoilValue(tableElementRefAtom);
  const scrollListener = useEvent((): void => {
    window.requestAnimationFrame(() => {
      if (!pagingRef.current || !relativeElement) {
        return;
      }

      pagingRef.current.style.transform = `translateX(${relativeElement.scrollLeft}px)`;
    });
  });

  useEffect(() => {
    if (!pagingRef.current || !relativeElement) {
      return;
    }

    relativeElement.addEventListener("scroll", scrollListener);

    return () => {
      relativeElement.removeEventListener("scroll", scrollListener);
    };
  }, [relativeElement, pagingRef, scrollListener]);
  useEffect(() => {
    if (!pagingRef.current || !relativeElement || !table) {
      return;
    }

    const isVisibleColumnNumberDecreased =
      visibleColumnNumber - prevVisibleColumnNumber.current < 0;

    const isOverScroll =
      relativeElement.scrollLeft + relativeElement.clientWidth >=
      table.clientWidth;

    if (isVisibleColumnNumberDecreased && isOverScroll) {
      pagingRef.current.style.transform = `translateX(${0}px)`;
    }

    prevVisibleColumnNumber.current = visibleColumnNumber;
  }, [relativeElement, pagingRef, table, visibleColumnNumber]);

  return null;
};
